import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AudioPlayer } from "@/components/audio-player";
import { ProductCard } from "@/components/product-card";
import { LoadingState } from "@/components/ui/loading-state";
import { ProductGridSkeleton } from "@/components/ui/product-skeleton";
import { AIBlob } from "@/components/ai-blob";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { useScrollAnimation, useStaggeredScrollAnimation } from "@/hooks/use-scroll-animation";
import { SEOHead } from "@/components/SEOHead";
import { 
  Play, 
  Brain, 
  Bath, 
  UserCheck, 
  Leaf, 
  Wind,
  Heart,
  Lightbulb,
  ArrowRight,
  Star,
  Calendar,
  User,
  Users,
  ChevronRight,
  Share2
} from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import googlePlayBadge from "@assets/Get_it_on_Android-removebg-preview_1772599859501.png";

export default function Home() {
  const { user, token, isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const [isHeroBlobSpeaking, setIsHeroBlobSpeaking] = useState(false);
  const heroBlobAudioRef = useRef<HTMLAudioElement | null>(null);
  const heroIntroPlayedRef = useRef(false);
  const heroRateLimitedRef = useRef(false);

  const playAudioWithRetry = useCallback(async (audio: HTMLAudioElement, maxRetries = 2): Promise<boolean> => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await audio.play();
        return true;
      } catch (err) {
        console.warn(`[Audio] play() attempt ${attempt + 1} failed:`, err);
        if (attempt < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 500));
        }
      }
    }
    try {
      console.log('[Audio] Trying Web Audio API fallback...');
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (ctx.state === 'suspended') await ctx.resume();
      const source = ctx.createMediaElementSource(audio);
      source.connect(ctx.destination);
      await audio.play();
      return true;
    } catch (err) {
      console.warn('[Audio] Web Audio API fallback also failed:', err);
      return false;
    }
  }, []);

  const playHeroBlobIntro = useCallback(async () => {
    if (isHeroBlobSpeaking || heroIntroPlayedRef.current) return;
    try {
      setIsHeroBlobSpeaking(true);
      const response = await fetch('/api/voice/hero-intro', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: user?.firstName || '' }),
      });
      if (!response.ok) {
        if (response.status === 429) { heroRateLimitedRef.current = true; }
        setIsHeroBlobSpeaking(false);
        return;
      }
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      heroBlobAudioRef.current = audio;
      audio.onplay = () => { heroIntroPlayedRef.current = true; };
      audio.onended = () => { setIsHeroBlobSpeaking(false); URL.revokeObjectURL(audioUrl); heroBlobAudioRef.current = null; };
      audio.onerror = () => { setIsHeroBlobSpeaking(false); URL.revokeObjectURL(audioUrl); heroBlobAudioRef.current = null; };
      const played = await playAudioWithRetry(audio);
      if (!played) {
        console.warn('[Hero] All audio play attempts failed, waiting for user click');
        setIsHeroBlobSpeaking(false);
      }
    } catch (err) {
      console.warn('[Hero] playHeroBlobIntro error:', err);
      setIsHeroBlobSpeaking(false);
    }
  }, [isHeroBlobSpeaking, user, playAudioWithRetry]);


  useEffect(() => {
    if (heroIntroPlayedRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !heroIntroPlayedRef.current) {
            playHeroBlobIntro();
            break;
          }
        }
      },
      { threshold: 0.3 }
    );
    const blobs = document.querySelectorAll('[data-hero-blob]');
    blobs.forEach(el => observer.observe(el));
    return () => { blobs.forEach(el => observer.unobserve(el)); if (heroBlobAudioRef.current) { heroBlobAudioRef.current.pause(); heroBlobAudioRef.current = null; } };
  }, [playHeroBlobIntro]);

  const { data: products = [] } = useQuery({
    queryKey: ["/api/products"],
  });

  // Fetch sound tracks for featured content
  const { data: soundTracks = [] } = useQuery<any[]>({
    queryKey: ["/api/sound-tracks"],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: langPrefData } = useQuery<{ language: string }>({
    queryKey: ["/api/user/language-preference"],
    queryFn: async () => {
      if (!token) return { language: 'hinglish' };
      const res = await fetch("/api/user/language-preference", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return { language: 'hinglish' };
      return res.json();
    },
    enabled: isAuthenticated && !!token,
    staleTime: 10 * 60 * 1000,
  });
  const userLanguage = (langPrefData?.language === 'hindi' ? 'hinglish' : langPrefData?.language) || 'hinglish';

  const { data: wellnessQuotes = [] } = useQuery<{ id: number; quote: string; author: string | null }[]>({
    queryKey: ["/api/wellness-quotes", userLanguage],
    queryFn: async () => {
      const res = await fetch(`/api/wellness-quotes?language=${userLanguage}`);
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
  });

  // State for rotating quotes
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

  // Rotate quotes every 4 seconds
  useEffect(() => {
    if (wellnessQuotes.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentQuoteIndex((prev) => (prev + 1) % wellnessQuotes.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [wellnessQuotes.length]);

  const currentQuote = wellnessQuotes[currentQuoteIndex];

  // Modified quote for HealthyOwl AI Mitr
  const displayQuote = currentQuote?.author === "HealthyOwl AI" 
    ? { ...currentQuote, author: "HealthyOwl AI Mitr" }
    : currentQuote;

  const { data: bookings = [] } = useQuery<any[]>({
    queryKey: ["/api/bookings", user?.id],
    enabled: isAuthenticated && !!token && !!user?.id,
    staleTime: 30000, // Cache for 30 seconds
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const response = await fetch("/api/bookings", {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }
      return response.json();
    },
  });

  const featuredProducts = (products as any[]).slice(0, 3);
  const featuredSoundTrack = (soundTracks as any[]).find((track: any) => track.category === "rain");

  // Get priority session: active first, then soonest upcoming
  const getNextBooking = () => {
    if (!Array.isArray(bookings) || bookings.length === 0) return null;

    const now = new Date();
    const validBookings = bookings
      .filter((booking: any) => {
        if (booking.status === 'cancelled' || booking.status === 'completed' || booking.status === 'no-show') return false;

        let sessionDateTime;
        if (booking.scheduledAt) {
          sessionDateTime = new Date(booking.scheduledAt);
        } else {
          sessionDateTime = new Date(`${booking.date} ${booking.time}`);
        }

        const sessionDuration = booking.sessionType === 'ai' ? 20 * 60 * 1000 : 
                               booking.sessionType === 'group' ? 60 * 60 * 1000 : 50 * 60 * 1000;
        const sessionEndTime = new Date(sessionDateTime.getTime() + sessionDuration);

        return sessionEndTime > now;
      });

    // Priority 1: Active sessions (isActive from backend)
    const activeSessions = validBookings.filter((b: any) => b.isActive);
    if (activeSessions.length > 0) {
      return activeSessions[0];
    }

    // Priority 2: Soonest upcoming session
    const upcoming = validBookings.sort((a: any, b: any) => {
      const dateA = a.scheduledAt ? new Date(a.scheduledAt) : new Date(`${a.date} ${a.time}`);
      const dateB = b.scheduledAt ? new Date(b.scheduledAt) : new Date(`${b.date} ${b.time}`);
      return dateA.getTime() - dateB.getTime();
    });

    return upcoming[0] || null;
  };

  // Get therapist/provider name
  const getTherapistName = (booking: any) => {
    if (booking.sessionType === 'ai') return 'HealthyOwl AI';
    if (booking.sessionType === 'group') return booking.therapistName || 'Group Facilitator';
    return booking.therapistName || 'Your Therapist';
  };

  // Get session description/title
  const getSessionDescription = (booking: any) => {
    if (booking.sessionType === 'ai') return 'Your compassionate AI wellness companion';
    if (booking.sessionType === 'group') return 'Group wellness session';
    return booking.therapistSpecialty || 'Personal Wellness Session';
  };

  // Get session link based on type
  const getSessionLink = (booking: any) => {
    if (booking.sessionType === 'ai') return `/ai-session?bookingId=${booking.id}`;
    return '/my-account/sessions';
  };

  const nextBooking = getNextBooking();

  // Display time until session (for UI only - NOT for join logic)
  const getTimeUntilSession = (booking: any) => {
    if (!booking) return null;

    const sessionDateTime = new Date(booking.scheduledAt);
    const now = new Date();
    const diff = sessionDateTime.getTime() - now.getTime();

    // Trust backend for canJoin and isActive - only calculate display message
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));

    let displayMessage = '';
    if (days > 0) {
      displayMessage = `${days}d ${hours}h`;
    } else if (hours > 0) {
      displayMessage = `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      displayMessage = `${minutes}m`;
    } else {
      displayMessage = 'Now';
    }

    return {
      canJoin: booking.canJoin, // Trust backend
      isActive: booking.isActive, // Trust backend
      message: booking.canJoin ? (booking.isActive ? 'Rejoin Session' : 'Join Now') : displayMessage
    };
  };

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Dynamic greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  // Check if user has an active AI session (status is active, confirmed, pending, or scheduled)
  const activeAISession = bookings?.find(
    (booking: any) => booking.sessionType === 'ai' && 
      (booking.status === 'active' || booking.status === 'confirmed' || booking.status === 'pending' || booking.status === 'scheduled')
  );
  const hasActiveAISession = isAuthenticated && !!activeAISession;

  // Check if user has completed any AI sessions
  const hasCompletedAISession = bookings?.some(
    (booking: any) => booking.sessionType === 'ai' && booking.status === 'completed'
  );

  // Determine AI CTA text and destination based on session state
  const aiCTAText = hasActiveAISession 
    ? "Rejoin Session" 
    : (hasCompletedAISession ? "Start AI Session" : "Try Free AI Mitr Session");
  const aiCTALink = hasActiveAISession ? `/ai-session?bookingId=${activeAISession.id}` : "/booking";

  const handleHeroBlobClick = () => {
    if (heroRateLimitedRef.current || heroIntroPlayedRef.current) {
      navigate(aiCTALink);
    } else {
      playHeroBlobIntro();
    }
  };

  // Animation hooks for different sections
  const heroSection = useScrollAnimation({ threshold: 0.2 });
  const featuresSection = useStaggeredScrollAnimation(4, { threshold: 0.1 });
  const therapySection = useStaggeredScrollAnimation(3, { threshold: 0.1 });
  const testimonialsSection = useStaggeredScrollAnimation(3, { threshold: 0.1 });
  const blogSection = useStaggeredScrollAnimation(3, { threshold: 0.1 });
  const productsSection = useStaggeredScrollAnimation(3, { threshold: 0.1 });

  // Fetch real therapists from database
  const { data: therapistsData = [] } = useQuery<any[]>({
    queryKey: ["/api/therapists", { limit: "3" }],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Use real therapist data, limit to 3 for home page
  const therapists = (therapistsData as any[]).slice(0, 3);

  return (
    <div className="min-h-screen">
      <SEOHead 
        title="HealthyOwl - AI-Powered Mental Wellness & Therapy Platform"
        description="Experience 24/7 personalized mental health support with AI therapy, guided meditation, sound therapy, and breathing exercises. Free AI sessions available."
      />
      {/* Mobile: Priority Session Card (Active > Upcoming) */}
      {isAuthenticated && nextBooking && (
        <section className="block md:hidden px-4 pt-6 pb-2">
          {(() => {
            const timeInfo = getTimeUntilSession(nextBooking);
            const isActive = timeInfo?.isActive;
            return (
              <Card className="border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                <CardContent className="p-4">
                  <div className="mb-3">
                    <p className="text-sm text-emerald-700 flex items-center gap-1">
                      <span>💚</span> Your Session ({nextBooking.sessionId || `#${nextBooking.id}`}) is with
                    </p>
                    <h3 className="text-lg font-bold text-foreground">
                      {getTherapistName(nextBooking)}
                      {nextBooking.sessionType === 'human' && nextBooking.therapistLicenseNumber && (
                        <span className="text-sm font-normal text-muted-foreground ml-2">({nextBooking.therapistLicenseNumber})</span>
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground">{nextBooking.therapistTitle || getSessionDescription(nextBooking)}</p>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center flex-wrap gap-1">
                      <span className="text-sm text-muted-foreground">{nextBooking.date} • {nextBooking.time}</span>
                      {isActive && (
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                          <span className="text-xs text-green-600 font-medium">(Session is Active)</span>
                        </div>
                      )}
                      {!timeInfo?.canJoin && !isActive && (
                        <span className="text-xs text-muted-foreground">• Starts in {timeInfo?.message}</span>
                      )}
                    </div>
                    {timeInfo?.canJoin ? (
                      <Link href={getSessionLink(nextBooking)}>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
                          {isActive ? 'Rejoin' : 'Join'}
                        </Button>
                      </Link>
                    ) : (
                      <Button size="sm" disabled className="bg-gray-300 text-gray-500 cursor-not-allowed w-full sm:w-auto">
                        Not Started
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </section>
      )}

      {/* Mobile: AI-Powered Wellness Hero (< 768px) */}
      <section className="block md:hidden px-4 py-8 pb-4 bg-gradient-to-br from-blue-500 to-purple-600 text-center">

        <h1 className="text-white text-3xl font-bold mb-3 leading-tight">
          Meet HealthyOwl AI Mitr<br />
          <span className="text-yellow-300 text-xl font-semibold">Your mind deserves a mitr (friend)</span>
        </h1>

        {/* Animated AI Blob */}
        <div className="flex justify-center my-4" data-hero-blob>
          <AIBlob isSpeaking={isHeroBlobSpeaking} size={140} className="drop-shadow-2xl" onClick={handleHeroBlobClick} />
        </div>

        <p className="text-white/90 text-base mb-2 leading-relaxed">
          Your judgment-free space, 24x7 available, completely private, and built to understand you the way you speak.
        </p>
        {/* Value Chips */}
        <h3 className="text-yellow-300 font-bold text-lg mb-3 italic">"Baat karo. Khulke. Kabhi bhi."</h3>
        <div className="space-y-2 mb-5 text-left">
          <p className="text-white/90 text-sm"><span className="font-bold">🤝 Aapko Samjhe</span> — Understands You. Built with Indian contexts, emotions, and languages.</p>
          <p className="text-white/90 text-sm"><span className="font-bold">🔒 Bro Code</span> — End-to-end encrypted. What you share here, stays here. Always.</p>
          <p className="text-white/90 text-sm"><span className="font-bold">⚡ Hamesha Aapke Saath</span> — 3 AM anxiety. Job, Relationships. Exams. Whenever you need it, AI Mitr is ready.</p>
        </div>

        {/* CTA Button */}
        <Link href={aiCTALink}>
          <Button size="lg" className="w-full bg-white text-blue-600 hover:bg-gray-100 font-bold">
            {hasActiveAISession ? "Rejoin Session" : "Start your Free AI Session"}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
        <p className="text-white/70 text-xs text-center mt-2">No waitlists. No stigma. No cost to start.</p>
      </section>

      {/* Mobile: What Happens in AI Mitr Session */}
      <section className="block md:hidden px-4 py-8 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-2 text-center italic">What Happens in AI Mitr Session?</h2>
          <p className="text-center text-muted-foreground mb-6 text-sm">Your First Conversation with Mitr</p>
          <div className="flex flex-col gap-4">
            <div className="relative bg-muted/50 rounded-2xl p-5 overflow-hidden flex items-center gap-4">
              <span className="text-7xl font-bold text-blue-500 opacity-15 select-none pointer-events-none shrink-0 leading-none">1</span>
              <div className="text-right flex-1">
                <h3 className="font-bold text-base mb-1">Just Say Hello</h3>
                <p className="text-muted-foreground text-sm">
                  No forms. No diagnosis questionnaires. No pressure. Just start talking — about your day, what's bothering you, or even something you can't quite explain yet.
                </p>
              </div>
            </div>
            <div className="relative bg-muted/50 rounded-2xl p-5 overflow-hidden flex items-center gap-4 flex-row-reverse">
              <span className="text-7xl font-bold text-emerald-500 opacity-15 select-none pointer-events-none shrink-0 leading-none">2</span>
              <div className="text-left flex-1">
                <h3 className="font-bold text-base mb-1">Mitr Listens and Reflects</h3>
                <p className="text-muted-foreground text-sm">
                  Mitr picks up on what you're saying, gently asks questions, and helps you put words to feelings you may have been carrying for a while.
                </p>
              </div>
            </div>
            <div className="relative bg-muted/50 rounded-2xl p-5 overflow-hidden flex items-center gap-4">
              <span className="text-7xl font-bold text-purple-500 opacity-15 select-none pointer-events-none shrink-0 leading-none">3</span>
              <div className="text-right flex-1">
                <h3 className="font-bold text-base mb-1">You Start to See Patterns</h3>
                <p className="text-muted-foreground text-sm">
                  Over time, Mitr helps you connect the dots — between situations, emotions, and reactions. That clarity itself is the beginning of feeling better.
                </p>
              </div>
            </div>
            <div className="relative bg-muted/50 rounded-2xl p-5 overflow-hidden flex items-center gap-4 flex-row-reverse">
              <span className="text-7xl font-bold text-orange-500 opacity-15 select-none pointer-events-none shrink-0 leading-none">4</span>
              <div className="text-left flex-1">
                <h3 className="font-bold text-base mb-1">You Decide What's Next</h3>
                <p className="text-muted-foreground text-sm">
                  Stay with Mitr. Explore the tools. Have follow up conversation with AI Mitr Or if you feel take the step to speak to a real Human Mitr. No pressure. No timeline. Your pace, your choice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile: How can HealthyOwl help? */}
      <section className="block md:hidden px-4 py-8 bg-gradient-to-b from-white to-gray-50">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground italic mb-2">What is HealthyOwl</h2>
          <p className="text-sm text-muted-foreground">Built for Indians, from India, with your context</p>
        </div>

        <div className="mb-6">
          <p className="text-sm text-foreground/90 leading-relaxed mb-2 font-medium">You're not alone. And you're not weak for needing support.</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Over 150 million Indians are struggling with their mental health right now. Most will never get help — not because they don't want to, but because the system wasn't built for them.
          </p>
        </div>

        <h3 className="text-lg font-bold text-foreground mb-4 text-center">The Barriers We're Breaking</h3>

        <div className="space-y-3">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-1">😶 "Log kya kahenge?"</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">84.5% of people who need mental health support never seek it. Stigma is the wall. We at HealthyOwl are removing it — Completely private, totally secure, no one who knows. Just you and Your AI Mitr.</p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-1">💸 "Therapy toh bahut mehnga hai."</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">One therapy session costs ₹1,500 to ₹3,000. For most Indians, that's not possible. HealthyOwl is free to start — and if you choose to go premium, it costs less than your weekly chai.</p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-1">⏳ "Koi milta hi nahi."</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">India has 0.3 psychiatrists per lakh people. The queues are long. The hours are short. AI Mitr doesn't have office hours; it's there at 3 AM, on a holiday, in the middle of a hard night.</p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-1">🗣️ "Yeh mujhe samjhega nahi."</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">Most wellness apps are built abroad and adapted for India as an afterthought. HealthyOwl is built from scratch for how Indians actually think, feel, and speak — Hindi, Hinglish, English, and more coming soon.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Mobile: How can HealthyOwl help? */}
      <section className="block md:hidden px-4 py-8 bg-white">
        <div className="text-center mb-5">
          <h2 className="text-2xl font-bold text-foreground italic mb-2">How can HealthyOwl help?</h2>
          <p className="text-sm text-muted-foreground italic">"Think of Mitr as the conversation before the conversation."</p>
        </div>

        <Card className="shadow-md">
          <CardContent className="p-5 space-y-4">
            <p className="text-sm text-foreground/90 leading-relaxed">
              It rarely starts with "I need therapy." It starts with a feeling you can't name, a weight that won't lift, a pattern you can't explain.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Mitr helps you figure out where it hurts — by simply being there while you think out loud. No diagnosis. No labels. Just clarity, at your own pace.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              And when you're ready for a human therapist — Mitr, HealthyOwl connects you with them privately, seamlessly.
            </p>
            <p className="text-sm text-foreground/70 italic leading-relaxed border-l-2 border-blue-400 pl-3">
              "Only 16% of Indians who need mental health care ever receive it." — NIMHANS
            </p>
            <p className="text-sm text-foreground/90 font-medium leading-relaxed">
              The first step is just a conversation.
            </p>
            <Link href={aiCTALink}>
              <Button size="lg" className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 font-bold mt-2">
                Start Talking to Mitr — It's Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </section>

      {/* Mobile: Wellness Quote Card */}
      <section className="block md:hidden px-4 py-4 bg-gray-50">
        <Card className="bg-card/50 backdrop-blur border-border/50">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Brain className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground text-sm mb-1">HealthyOwl AI Mitr</h3>
                <p className="text-xs text-foreground/90 font-medium leading-relaxed">
                  "{currentQuote?.quote || "Take a deep breath. You are stronger than you think."}"
                </p>
              </div>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`"${currentQuote?.quote || "Take a deep breath. You are stronger than you think."}" - HealthyOwl\n\nCheck out https://healthyowl.in an AI powered 24x7 wellness platform`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 hover:bg-green-600 transition-colors self-center flex-shrink-0"
                title="Share on WhatsApp"
              >
                <SiWhatsapp className="h-3.5 w-3.5 text-white" />
              </a>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Mobile: Quick Actions Section */}
      <section className="block md:hidden px-4 py-8 bg-gray-50">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-2xl font-bold text-foreground text-center mb-6">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-8">
            <Link href="/booking">
              <Card className="p-4 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-0 flex flex-col items-center justify-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-3">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm">Book Session</h3>
                  <p className="text-xs text-muted-foreground">Schedule your next wellness session</p>
                </CardContent>
              </Card>
            </Link>

            <Link href={aiCTALink}>
              <Card className="p-4 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-0 flex flex-col items-center justify-center">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mb-3">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm">{hasActiveAISession ? "Rejoin Session" : "AI Session (FREE)"}</h3>
                  <p className="text-xs text-muted-foreground">{hasActiveAISession ? "Continue your chat" : "Instant & Free AI support"}</p>
                </CardContent>
              </Card>
            </Link>

            <Link href="/sound-therapy">
              <Card className="p-4 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-0 flex flex-col items-center justify-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mb-3">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm">Calm Sounds</h3>
                  <p className="text-xs text-muted-foreground">Immerse yourself in healing frequencies</p>
                </CardContent>
              </Card>
            </Link>

            <Link href="/breathing-exercises">
              <Card className="p-4 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-0 flex flex-col items-center justify-center">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-3">
                    <Wind className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm">Breathing</h3>
                  <p className="text-xs text-muted-foreground">Practice mindful breathing</p>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Today's Recommendation - Mobile */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Today's Recommendation</h2>
            <Link href="/mindfulness">
              <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <CardContent className="p-0">
                  <h3 className="text-white text-xl font-bold mb-2">5-Minute Calm</h3>
                  <p className="text-white/90 mb-4">Start your day with mindful breathing</p>
                  <div className="flex items-center">
                    <Play className="h-6 w-6 text-white mr-2" />
                    <span className="text-white font-semibold">Start Session</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Desktop: AI Therapist Hero Section (>= 768px) */}
      <section className="hidden md:block px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 bg-gradient-to-br from-blue-500 to-purple-600">
        <div className="mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            <div className="space-y-4 sm:space-y-5">

              <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
                Meet HealthyOwl AI Mitr<br />
                <span className="text-yellow-300 text-lg sm:text-xl lg:text-2xl font-semibold">Your mind deserves a mitr (friend)</span>
              </h1>

              <p className="text-white/90 text-base sm:text-lg leading-relaxed">
                Your judgment-free space, 24x7 available, completely private, and built to understand you the way you speak.
              </p>

              {/* Value Chips */}
              <h3 className="text-yellow-300 font-bold text-xl italic mb-3">"Baat karo. Khulke. Kabhi bhi."</h3>
              <div className="space-y-2">
                <p className="text-white/90 text-sm sm:text-base"><span className="font-bold">🤝 Aapko Samjhe</span> — Understands You. Built with Indian contexts, emotions, and languages.</p>
                <p className="text-white/90 text-sm sm:text-base"><span className="font-bold">🔒 Bro Code</span> — End-to-end encrypted. What you share here, stays here. Always.</p>
                <p className="text-white/90 text-sm sm:text-base"><span className="font-bold">⚡ Hamesha Aapke Saath</span> — 3 AM anxiety. Job, Relationships. Exams. Whenever you need it, AI Mitr is ready.</p>
              </div>

              {/* CTA Button */}
              <div>
                <Link href={aiCTALink}>
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-bold text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4">
                    {aiCTAText}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <p className="text-white/60 text-sm mt-2">No waitlists. No stigma. No cost to start.</p>
              </div>
            </div>

            <div className="relative mb-4">
              <div className="relative bg-gradient-to-br from-blue-400/30 to-purple-600/30 rounded-3xl p-6 shadow-2xl flex items-center justify-center min-h-[320px]">
                <div className="text-center">
                  <div data-hero-blob>
                    <AIBlob isSpeaking={isHeroBlobSpeaking} size={280} className="mx-auto mb-6 drop-shadow-2xl cursor-pointer" onClick={handleHeroBlobClick} />
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-md mx-auto">
                    <h3 className="text-white font-bold text-2xl mb-2">Your mind deserves a mitr (friend)</h3>
                    <p className="text-white/90 text-sm">Your judgment-free space, always available and completely private</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Desktop: Priority Session Card / Wellness Tip */}
      <section className="hidden md:block px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <div className="mx-auto max-w-4xl">
          {/* Priority Session Card - Desktop */}
          {isAuthenticated && nextBooking && (() => {
            const timeInfo = getTimeUntilSession(nextBooking);
            const isActive = timeInfo?.isActive;
            return (
              <Card className="border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-emerald-700 flex items-center gap-1 mb-1">
                        <span>💚</span> Your Session ({nextBooking.sessionId || `#${nextBooking.id}`}) is with
                      </p>
                      <h3 className="text-xl font-bold text-foreground">
                        {getTherapistName(nextBooking)}
                        {nextBooking.sessionType === 'human' && nextBooking.therapistLicenseNumber && (
                          <span className="text-sm font-normal text-muted-foreground ml-2">({nextBooking.therapistLicenseNumber})</span>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">{nextBooking.therapistTitle || getSessionDescription(nextBooking)}</p>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">{nextBooking.date} • {nextBooking.time}</span>
                        {isActive && (
                          <div className="flex items-center space-x-1">
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-sm text-green-600 font-medium">(Session is Active)</span>
                          </div>
                        )}
                        {!timeInfo?.canJoin && !isActive && (
                          <span className="text-sm text-muted-foreground">• Starts in {timeInfo?.message}</span>
                        )}
                      </div>
                    </div>
                    {timeInfo?.canJoin ? (
                      <Link href={getSessionLink(nextBooking)}>
                        <Button className="bg-emerald-600 hover:bg-emerald-700">
                          {isActive ? 'Rejoin Session' : 'Join Session'}
                        </Button>
                      </Link>
                    ) : (
                      <Button disabled className="bg-gray-300 text-gray-500 cursor-not-allowed">
                        Not Started
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Wellness Quote Card - Desktop */}
          {(!isAuthenticated || !nextBooking) && (
            <Card className="bg-card/50 backdrop-blur border-border/50">
              <CardContent className="p-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Brain className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">HealthyOwl AI Mitr</h3>
                    <p className="text-sm text-foreground/90 font-medium leading-relaxed transition-opacity duration-500">
                      "{currentQuote?.quote || "Take a deep breath. You are stronger than you think."}"
                    </p>
                  </div>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`"${currentQuote?.quote || "Take a deep breath. You are stronger than you think."}" - HealthyOwl\n\nCheck out https://healthyowl.in an AI powered 24x7 wellness platform`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500 hover:bg-green-600 transition-colors self-center flex-shrink-0"
                    data-testid="button-share-quote-whatsapp"
                    title="Share on WhatsApp"
                  >
                    <SiWhatsapp className="h-4 w-4 text-white" />
                  </a>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Desktop: What Happens in AI Mitr Session */}
      <section className="hidden md:block px-4 sm:px-6 lg:px-8 py-12 lg:py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-3xl sm:text-4xl font-bold mb-2 text-center italic">What Happens in AI Mitr Session?</h2>
          <p className="text-center text-muted-foreground mb-10 text-lg">Your First Conversation with Mitr</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative bg-muted/50 rounded-2xl p-6">
              <div className="bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mb-4">1</div>
              <h3 className="font-bold text-lg mb-2">Just Say Hello</h3>
              <p className="text-muted-foreground text-sm">
                No forms. No diagnosis questionnaires. No pressure. Just start talking — about your day, what's bothering you, or even something you can't quite explain yet.
              </p>
            </div>
            <div className="relative bg-muted/50 rounded-2xl p-6">
              <div className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mb-4">2</div>
              <h3 className="font-bold text-lg mb-2">Mitr Listens and Reflects</h3>
              <p className="text-muted-foreground text-sm">
                Mitr picks up on what you're saying, gently asks questions, and helps you put words to feelings you may have been carrying for a while.
              </p>
            </div>
            <div className="relative bg-muted/50 rounded-2xl p-6">
              <div className="bg-purple-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mb-4">3</div>
              <h3 className="font-bold text-lg mb-2">You Start to See Patterns</h3>
              <p className="text-muted-foreground text-sm">
                Over time, Mitr helps you connect the dots — between situations, emotions, and reactions. That clarity itself is the beginning of feeling better.
              </p>
            </div>
            <div className="relative bg-muted/50 rounded-2xl p-6">
              <div className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mb-4">4</div>
              <h3 className="font-bold text-lg mb-2">You Decide What's Next</h3>
              <p className="text-muted-foreground text-sm">
                Stay with Mitr. Explore the tools. Have follow up conversation with AI Mitr Or if you feel take the step to speak to a real Human Mitr. No pressure. No timeline. Your pace, your choice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Desktop: How can HealthyOwl help? */}
      <section className="hidden md:block px-4 sm:px-6 lg:px-8 py-12 lg:py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground italic mb-3">What is HealthyOwl</h2>
            <p className="text-lg text-muted-foreground">Built for Indians, from India, with your context</p>
          </div>

          <div className="max-w-3xl mx-auto mb-10 text-center">
            <p className="text-base text-foreground/90 leading-relaxed mb-2 font-medium">You're not alone. And you're not weak for needing support.</p>
            <p className="text-base text-muted-foreground leading-relaxed">
              Over 150 million Indians are struggling with their mental health right now. Most will never get help — not because they don't want to, but because the system wasn't built for them.
            </p>
          </div>

          <h3 className="text-2xl font-bold text-foreground mb-8 text-center">The Barriers We're Breaking</h3>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <h4 className="font-bold text-foreground text-lg mb-2">😶 "Log kya kahenge?"</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">84.5% of people who need mental health support never seek it. Stigma is the wall. We at HealthyOwl are removing it — Completely private, totally secure, no one who knows. Just you and Your AI Mitr.</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <h4 className="font-bold text-foreground text-lg mb-2">💸 "Therapy toh bahut mehnga hai."</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">One therapy session costs ₹1,500 to ₹3,000. For most Indians, that's not possible. HealthyOwl is free to start — and if you choose to go premium, it costs less than your weekly chai.</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <h4 className="font-bold text-foreground text-lg mb-2">⏳ "Koi milta hi nahi."</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">India has 0.3 psychiatrists per lakh people. The queues are long. The hours are short. AI Mitr doesn't have office hours; it's there at 3 AM, on a holiday, in the middle of a hard night.</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <h4 className="font-bold text-foreground text-lg mb-2">🗣️ "Yeh mujhe samjhega nahi."</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">Most wellness apps are built abroad and adapted for India as an afterthought. HealthyOwl is built from scratch for how Indians actually think, feel, and speak — Hindi, Hinglish, English, and more coming soon.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Desktop: How can HealthyOwl help? */}
      <section className="hidden md:block px-4 sm:px-6 lg:px-8 py-12 lg:py-16 bg-white">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground italic mb-3">How can HealthyOwl help?</h2>
            <p className="text-lg text-muted-foreground italic">"Think of Mitr as the conversation before the conversation."</p>
          </div>

          <Card className="shadow-md max-w-3xl mx-auto">
            <CardContent className="p-8 space-y-5">
              <p className="text-base text-foreground/90 leading-relaxed">
                It rarely starts with "I need therapy." It starts with a feeling you can't name, a weight that won't lift, a pattern you can't explain.
              </p>
              <p className="text-base text-foreground/90 leading-relaxed">
                Mitr helps you figure out where it hurts — by simply being there while you think out loud. No diagnosis. No labels. Just clarity, at your own pace.
              </p>
              <p className="text-base text-foreground/90 leading-relaxed">
                And when you're ready for a human therapist — Mitr, HealthyOwl connects you with them privately, seamlessly.
              </p>
              <p className="text-base text-foreground/70 italic leading-relaxed border-l-4 border-blue-400 pl-4">
                "Only 16% of Indians who need mental health care ever receive it." — NIMHANS
              </p>
              <p className="text-base text-foreground/90 font-medium leading-relaxed">
                The first step is just a conversation.
              </p>
              <Link href={aiCTALink}>
                <Button size="lg" className="bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 font-bold text-lg px-8 py-4 mt-2">
                  Start Talking to Mitr — It's Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Services Section (Hidden on mobile) */}
      <section ref={therapySection.ref} className="hidden md:block px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <div className={`text-center mb-16 animate-on-scroll ${
            therapySection.visibleItems[0] ? 'animate-fade-in-up' : ''
          }`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Wellness Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive wellness solutions combining ancient wisdom with modern science
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Calm Sounds */}
            <Card className={`wellness-card animate-on-scroll ${
              therapySection.visibleItems[0] ? 'animate-fade-in-up animate-delay-100' : ''
            }`}>
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mb-6">
                <Play className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Calm Sounds</h3>
              <p className="text-muted-foreground mb-6">
                Immerse yourself in healing frequencies, rain sounds, and sleep music scientifically proven to reduce stress.
              </p>

              {featuredSoundTrack && (
                <div className="mb-4">
                  <div className="bg-gradient-to-r from-emerald-100 to-teal-100 rounded-xl p-4 mb-3">
                    <h4 className="font-semibold text-gray-900 mb-2">Featured Track</h4>
                    <p className="text-sm text-gray-700 mb-3">{featuredSoundTrack.title}</p>
                    <AudioPlayer 
                      track={featuredSoundTrack}
                      className="bg-white/80"
                    />
                  </div>
                </div>
              )}

              <Link href="/sound-therapy">
                <Button className="w-full wellness-button">
                  Explore More Sounds
                </Button>
              </Link>
            </Card>


            {/* Human Consultation */}
            <Card className="wellness-card relative">
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mb-6">
                <UserCheck className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Book session with HealthyOwl Mitr</h3>
              <p className="text-muted-foreground mb-6">
                Connect with verfied Human Mitr for personalized one-on-one sessions.
              </p>

              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-3 p-3 bg-accent/10 rounded-xl">
                  <div className="w-8 h-8 bg-secondary/30 rounded-full flex items-center justify-center">
                    <Brain className="h-4 w-4 text-secondary-foreground" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">HealthyOwl AI Session</h4>
                    <p className="text-gray-600">Personalized recommendations with AI support</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-accent/10 rounded-xl">
                  <div className="w-8 h-8 bg-primary/30 rounded-full flex items-center justify-center">
                    <UserCheck className="h-4 w-4 text-primary-foreground" />
                  </div>
                  <div>
                    <span className="font-medium text-sm">Talk to a Human Mitr</span>
                    <p className="text-xs text-muted-foreground">Verfied Companion</p>
                  </div>
                </div>
              </div>

              <Link href="/therapists"> {/* Updated link */}
                <Button className="w-full wellness-button">
                  Book Session
                </Button>
              </Link>
            </Card>


            {/* Breathing Techniques */}
            <Card className="wellness-card">
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mb-6">
                <Wind className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Breathing Techniques</h3>
              <p className="text-muted-foreground mb-6">
                Practice ancient breathing methods proven to reduce anxiety and improve mental clarity.
              </p>

              <Card className="bg-accent/20 p-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground mb-1">4-7-8</div>
                  <div className="text-sm text-muted-foreground">Inhale - Hold - Exhale</div>
                </div>
              </Card>

              <Link href="/mindfulness">
                <Button className="w-full wellness-button">
                  Start Breathing
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Mobile (< 768px) */}
      <section className="block md:hidden px-4 py-8 bg-gradient-to-b from-emerald-50 to-white">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-3">
              What Our Community Says
            </h2>
            <p className="text-base text-muted-foreground">
              Real stories from people who've transformed their wellness journey
            </p>
          </div>

          <div className="space-y-4">
            {/* Testimonial 1 - Mobile */}
            <Card className="p-5 bg-white shadow-md">
              <CardContent className="space-y-3">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  "The HealthyOwl Mitr AI sessions have been life-changing. I can access support whenever I need it, and the personalized approach really understands my needs. It's like having my friend available 24/7."
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                    MS
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Mahir Singh</div>
                    <div className="text-xs text-gray-500">Finance Professional</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 - Mobile */}
            <Card className="p-5 bg-white shadow-md">
              <CardContent className="space-y-3">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  "The breathing exercises and listening to calm sound have completely transformed my sleep quality. I used to struggle with insomnia, but now I fall asleep peacefully every night."
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold">
                    TG
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Tara Gupta</div>
                    <div className="text-xs text-gray-500">College Student</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 - Mobile */}
            <Card className="p-5 bg-white shadow-md">
              <CardContent className="space-y-3">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  The group sessions create such a supportive community. Sharing the wellness journey with others who understand has been incredibly healing and motivating.
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center text-white font-semibold">
                    SG
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Siya Garg</div>
                    <div className="text-xs text-gray-500">Entrepreneur</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Link href="/booking">
              <Button size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700">
                Start Your Journey Today
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Download Android App Section - Desktop */}
      <section className="hidden md:block px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-7xl">
          <Card className="wellness-card">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Download Android App
              </h3>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Take your wellness journey on the go. Download the HealthyOwl app on Android for easy access anytime, anywhere.
              </p>
              <a href="https://play.google.com/store/apps/details?id=in.healthyowl.app" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
                <img src={googlePlayBadge} alt="Get it on Google Play" className="h-48 mx-auto" />
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials Section - Desktop (>= 768px) */}
      <section ref={testimonialsSection.ref} className="hidden md:block px-4 py-16 bg-gradient-to-b from-sage-50 to-white">
        <div className="mx-auto max-w-7xl">
          <div className={`text-center mb-12 animate-on-scroll ${
            testimonialsSection.visibleItems[0] ? 'animate-fade-in-up' : ''
          }`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              What Our Community Says
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Real stories from people who've transformed their wellness journey with HealthyOwl
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Testimonial 1 */}
            <Card className={`relative p-6 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 animate-on-scroll ${
              testimonialsSection.visibleItems[0] ? 'animate-fade-in-up animate-delay-100' : ''
            }`}>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "The HealthyOwl Mitr AI sessions have been life-changing. I can access support whenever I need it, and the personalized approach really understands my needs. It's like having my friend available 24/7."
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                    MS
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Mahir Singh</div>
                    <div className="text-sm text-gray-500">Finance Professional</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className={`relative p-6 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 animate-on-scroll ${
              testimonialsSection.visibleItems[1] ? 'animate-fade-in-up animate-delay-200' : ''
            }`}>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "The breathing exercises and listening to calm sound have completely transformed my sleep quality. I used to struggle with insomnia, but now I fall asleep peacefully every night."
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold">
                    TG
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Tara Gupta</div>
                    <div className="text-sm text-gray-500">College Student</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className={`relative p-6 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 animate-on-scroll ${
              testimonialsSection.visibleItems[2] ? 'animate-fade-in-up animate-delay-300' : ''
            }`}>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed">
                  The group sessions create such a supportive community. Sharing the wellness journey with others who understand has been incredibly healing and motivating.
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center text-white font-semibold">
                    SG
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Siya Garg</div>
                    <div className="text-sm text-gray-500">Entrepreneur</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link href="/booking">
              <Button size="lg" className="wellness-button">
                Start Your Journey Today
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>



      {/* Featured Products - Temporarily Hidden 
      <section ref={productsSection.ref} className="px-4 py-16 bg-accent/5">
        <div className="mx-auto max-w-7xl">
          <div className={`text-center mb-12 animate-on-scroll ${
            productsSection.visibleItems[0] ? 'animate-fade-in-up' : ''
          }`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Shop
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Curated wellness products combining science-backed solutions with traditional wisdom
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProducts.map((product, index) => (
              <div 
                key={product.id}
                className={`animate-on-scroll ${
                  productsSection.visibleItems[index] ? `animate-fade-in-up animate-delay-${(index + 1) * 100}` : ''
                }`}
              >
                <ProductCard 
                  product={product} 
                  className="bg-accent/20"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link href="/shop">
              <Button size="lg" className="wellness-button">
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      */}

      {/* Therapists Section (New Section) */}
      <section className="hidden md:block px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Connect to professionals
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Connect with a diverse team of professionals to seek advice without any judgement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {therapists.map((therapist, index) => (
              <Card key={therapist.id} className={`relative p-6 shadow-lg hover:shadow-xl transition-shadow duration-300`}>
                <CardContent className="flex flex-col items-center text-center">
                  <Avatar className="w-32 h-32 mb-4 border-4 border-primary/20">
                    <AvatarImage src={therapist.avatar || ''} alt={therapist.name} />
                    <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                      {therapist.name ? therapist.name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'T'}
                    </AvatarFallback>
                  </Avatar>
                  <h4 className="text-xl font-bold text-foreground mb-1">
                    {therapist.name}
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">{therapist.title}</p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {therapist.experience}
                  </p>
                  <div className="flex flex-wrap justify-center gap-1 mb-6">
                    {therapist.specialty && (
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                        {therapist.specialty}
                      </span>
                    )}
                  </div>
                  {therapist.slug ? (
                    <Link href={`/therapist-profile/${therapist.slug}`}>
                      <Button className="w-full wellness-button">
                        View Profile
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  ) : (
                    <Button className="w-full wellness-button" disabled>
                      Profile Unavailable
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Safety & Security Benefits Card */}
          <Card className="wellness-card mt-12">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
                Your Safety & Privacy Matters
              </h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserCheck className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">Verified Listeners</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    All HealthyOwl Human Mitr are verified for your peace of mind
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-8 w-8 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">100% Confidential</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Your sessions and personal information are confidential and never shared with third parties
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Leaf className="h-8 w-8 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">Safe Environment</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Judgment-free sessions in a secure platform designed for your comfort and wellbeing
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>

    </div>
  );
}